import React, { useState, useEffect, useCallback } from 'react';
import {
  uploadLossRun,
  listLossRuns,
  analyzeLossRun,
  deleteLossRun,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  Upload,
  Loader2,
  FileText,
  Trash2,
  RefreshCw,
  Calendar,
} from 'lucide-react';

export default function LossRuns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    file: null,
    description: '',
    period_start: '',
    period_end: '',
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listLossRuns();
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load loss runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error('Choose a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('description', form.description || '');
      fd.append('period_start', form.period_start || '');
      fd.append('period_end', form.period_end || '');
      const res = await uploadLossRun(fd);
      toast.success('Loss run uploaded. Running AI analysis…');
      setShowUpload(false);
      setForm({ file: null, description: '', period_start: '', period_end: '' });
      await fetchItems();
      // Auto-run analysis
      if (res?.data?.id) {
        runAnalysis(res.data.id, true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runAnalysis = async (id, silent = false) => {
    setAnalyzingId(id);
    try {
      const res = await analyzeLossRun(id);
      const a = res.data?.analysis;
      if (a?.error) {
        toast.error(`Analysis failed: ${a.error}`);
      } else if (!silent) {
        toast.success('Analysis complete');
      } else {
        toast.success('AI analysis complete');
      }
      setExpandedId(id);
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLossRun(id);
      toast.success('Loss run deleted');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="loss-runs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-3">
            <TrendingDown className="w-8 h-8 text-primary" />
            Loss Run Analyzer
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Upload your loss runs and get AI-powered insights — trends, patterns, and actionable risk-reduction
            recommendations. This is a client value-add, separate from underwriting analysis.
          </p>
        </div>
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogTrigger asChild>
            <Button className="btn-primary-glow" data-testid="upload-loss-run-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Loss Run
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="upload-loss-run-dialog">
            <DialogHeader>
              <DialogTitle>Upload a Loss Run Report</DialogTitle>
              <DialogDescription>PDF, DOCX, CSV, or XLSX. We'll extract text and analyze it.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>File *</Label>
                <Input
                  type="file"
                  accept=".pdf,.docx,.csv,.xlsx,.xls"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  required
                  data-testid="loss-run-file-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Period Start</Label>
                  <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Period End</Label>
                  <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional notes" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={uploading} className="btn-primary-glow" data-testid="submit-loss-run-upload">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Upload & Analyze
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-base">No loss runs uploaded yet.</p>
            <p className="text-sm">Upload your first loss run to see AI-powered insights.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((it) => (
            <LossRunCard
              key={it.id}
              item={it}
              expanded={expandedId === it.id}
              onToggle={() => setExpandedId(expandedId === it.id ? null : it.id)}
              onAnalyze={() => runAnalysis(it.id)}
              onDelete={() => handleDelete(it.id)}
              analyzing={analyzingId === it.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LossRunCard({ item, expanded, onToggle, onAnalyze, onDelete, analyzing }) {
  const a = item.analysis;
  const statusBadge = (() => {
    if (item.analysis_status === 'completed') return <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">Analyzed</Badge>;
    if (item.analysis_status === 'failed') return <Badge variant="destructive">Failed</Badge>;
    return <Badge variant="outline">Not analyzed</Badge>;
  })();

  return (
    <Card className="bg-card border-border" data-testid={`loss-run-card-${item.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg break-words">{item.original_filename}</CardTitle>
            <CardDescription className="flex items-center gap-3 mt-1 text-xs flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(item.created_at).toLocaleDateString()}
              </span>
              {item.period_start && <span>Period: {item.period_start} → {item.period_end || '—'}</span>}
              <span>{Math.round((item.file_size || 0) / 1024)} KB</span>
              {statusBadge}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onAnalyze} disabled={analyzing} data-testid={`analyze-${item.id}`}>
              {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {item.analysis_status === 'completed' ? 'Re-analyze' : 'Analyze'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onToggle} data-testid={`toggle-${item.id}`}>
              {expanded ? 'Hide' : 'View'}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-destructive" data-testid={`delete-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this loss run?</AlertDialogTitle>
                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-4">
          {!a ? (
            <p className="text-sm text-muted-foreground">No analysis yet. Click "Analyze" to run AI insights.</p>
          ) : (
            <>
              {a.executive_summary && (
                <div className="p-3 rounded-md border border-purple-500/30 bg-purple-500/5">
                  <div className="flex items-center gap-2 mb-1 text-purple-700 dark:text-purple-300 font-semibold text-sm">
                    <Sparkles className="w-4 h-4" /> Executive Summary
                  </div>
                  <p className="text-sm">{a.executive_summary}</p>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                {(a.total_claims_estimate || a.total_incurred_estimate) && (
                  <Card className="bg-secondary/30 border-border">
                    <CardContent className="py-3">
                      <div className="text-xs text-muted-foreground">At a glance</div>
                      <div className="mt-1 text-sm">
                        {a.total_claims_estimate != null && <div>Claims: <span className="font-semibold">{a.total_claims_estimate}</span></div>}
                        {a.total_incurred_estimate && <div>Total Incurred: <span className="font-semibold">{a.total_incurred_estimate}</span></div>}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {!!(a.top_causes || []).length && (
                  <Card className="bg-secondary/30 border-border">
                    <CardContent className="py-3">
                      <div className="text-xs text-muted-foreground mb-1">Top causes</div>
                      <ul className="text-sm space-y-1">
                        {a.top_causes.map((c, i) => (
                          <li key={i}>
                            <span className="font-medium">{c.cause}</span>{typeof c.share_pct === 'number' ? ` — ${c.share_pct}%` : ''}
                            {c.notes && <span className="text-muted-foreground"> · {c.notes}</span>}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
              {!!(a.trends || []).length && (
                <InsightList title="Trends" icon={<TrendingUp className="w-4 h-4" />} items={a.trends} />
              )}
              {!!(a.high_risk_patterns || []).length && (
                <InsightList title="High-Risk Patterns" icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} items={a.high_risk_patterns} />
              )}
              {!!(a.recommendations || []).length && (
                <InsightList title="Recommendations" icon={<Sparkles className="w-4 h-4 text-primary" />} items={a.recommendations} highlight />
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function InsightList({ title, icon, items, highlight }) {
  return (
    <div className={`p-3 rounded-md border ${highlight ? 'border-primary/30 bg-primary/5' : 'border-border bg-secondary/20'}`}>
      <div className="flex items-center gap-2 mb-2 font-semibold text-sm">{icon} {title}</div>
      <ul className="text-sm list-disc pl-5 space-y-1">
        {items.map((t, i) => (<li key={i}>{t}</li>))}
      </ul>
    </div>
  );
}
